import React, { createContext, useEffect, useRef } from 'react';
import {
    AccountContracts,
    ContractDisplays,
    CustomerServiceRepresentative,
    CustomerServiceRepresentativeAssignedAccounts,
    MonitoringLog,
    MonitoringLogsByAccountNumber,
    MonitoringLogsByUser,
    MonitoringStatusItem,
} from '../../../Controllers/CustomerServiceDashboard/CustomerServiceDashboard';
import useActionReducer from '../../hooks/useActionReducer';
import ActionHandlers, { ActionType } from './actions';
import { getAllMonitoringLogs, getCustomerServiceAssignmentsAsync } from './CustomerServiceDashboardApiHandler';
import { useStore } from '../../hooks/use-store';
import { ActiveDisplay } from '../../Displays/ActiveDisplay';
import { toArray } from 'Mri.Web.Common';
import Account = Mri.Accounts.Account;

interface CustomerServiceDashboardStateLoadingState {
    accounts: boolean;
    monitoringLogs: boolean;
}
export interface CustomerServiceDashboardState {
    readonly monitoringLogs: MonitoringLog[];
    readonly monitoringLogsByUser: MonitoringLogsByUser;
    readonly monitoringLogsByAccountNumber: MonitoringLogsByAccountNumber;
    readonly monitoringStatusAccountList: MonitoringStatusItem[];
    readonly loading: CustomerServiceDashboardStateLoadingState;
    readonly error: any;
    readonly selectedLog: MonitoringLog | null;
    readonly selectedContract: Mri.Contracts.Contract | null;
    readonly selectedDisplay: ActiveDisplay | null;
    readonly accounts: Account[];
    readonly accountsVisibleToUser: Account[];
    readonly contracts: Mri.Contracts.Contract[];
    readonly accountContracts: AccountContracts;
    readonly displaysByContract: ContractDisplays;
    readonly customerServiceReps: CustomerServiceRepresentative[];
    readonly repAssignedAccounts: CustomerServiceRepresentativeAssignedAccounts;
}

export interface CustomerServiceDashboardContextProps {
    state: CustomerServiceDashboardState | any;
    dispatch: React.Dispatch<any> | null;
}

const initialState: CustomerServiceDashboardState = {
    monitoringLogs: [],
    monitoringLogsByUser: {},
    monitoringLogsByAccountNumber: {},
    monitoringStatusAccountList: [],
    loading: {
        accounts: false,
        monitoringLogs: false,
    },
    error: null,
    selectedLog: null,
    selectedContract: null,
    selectedDisplay: null,
    accounts: [],
    accountsVisibleToUser: [],
    contracts: [],
    accountContracts: {},
    displaysByContract: {},
    customerServiceReps: [],
    repAssignedAccounts: {},
};

export const CustomerServiceDashboardContext = createContext<CustomerServiceDashboardContextProps>({
    state: initialState,
    dispatch: () => null,
});

export interface CustomerServiceDashboardProviderProps {
    readonly children: any;
}

export const CustomerServiceDashboardProvider = ({ children }: CustomerServiceDashboardProviderProps) => {
    const { state, dispatch } = useActionReducer(initialState, ActionHandlers);
    const isMounted = useRef(true);
    const { contractsStore, accountsStore, accountStore, displaysStore } = useStore();
    const { user } = accountStore;
    // state.contracts = {...toPlainObject(contractsStore.accountContracts)};

    const fetchMonitoringLogs = async (abortController: AbortController) => {
        const { signal } = abortController;
        try {
            dispatch({ type: ActionType.FETCH_MONITORING_LOGS_REQUEST, payload: null });
            const response = await getAllMonitoringLogs({ signal });
            if (isMounted.current) {
                dispatch({ type: ActionType.FETCH_MONITORING_LOGS_SUCCESS, payload: response });
            }
        } catch (error) {
            if (isMounted.current) dispatch({ type: ActionType.FETCH_MONITORING_LOGS_FAILURE, payload: error });
        }
    };
    const mapContractsToAccounts = () => {
        state.contracts.forEach((contract: any) => {
            state.accountContracts[contract.accountNumber] = [
                ...state.accountContracts[contract.accountNumber],
                contract,
            ];
        });
    };

    const mapAccountsVisibleToUsers = () => {
        if (!user) return;
        const accountsVisibleToUser = state.accounts.filter(
            (account: Account) => state.repAssignedAccounts[user?.userName]?.indexOf(account.accountNumber) > -1
        );
        dispatch({ type: ActionType.SET_ACCOUNTS_VISIBLE_TO_USER, payload: accountsVisibleToUser });
    };

    const fetchAllAccountsViaStore = async () => {
        try {
            if (isMounted.current) dispatch({ type: ActionType.FETCH_ALL_ACCOUNTS_REQUEST, payload: null });
            await accountsStore.loadMonitoringActiveAsync();
            if (isMounted.current)
                dispatch({
                    type: ActionType.FETCH_ALL_ACCOUNTS_SUCCESS,
                    payload: toArray(accountsStore.monitoringActiveAccounts),
                });
        } catch (error) {
            if (isMounted.current) dispatch({ type: ActionType.FETCH_ALL_ACCOUNTS_FAILURE, payload: error });
            console.error(error);
        }
    };

    const fetchAccountAssignments = async (abortController: AbortController) => {
        if (!dispatch || !user) return;
        const { signal } = abortController;
        try {
            if (isMounted.current)
                dispatch({ type: ActionType.FETCH_CUSTOMER_SERVICE_REP_ASSIGNED_ACCOUNTS_REQUEST, payload: null });
            const response = await getCustomerServiceAssignmentsAsync(user?.userName, { signal });
            if (isMounted.current)
                dispatch({
                    type: ActionType.FETCH_CUSTOMER_SERVICE_REP_ASSIGNED_ACCOUNTS_SUCCESS,
                    payload: { [user?.userName]: response },
                });
        } catch (error) {
            if (isMounted.current)
                dispatch({ type: ActionType.FETCH_CUSTOMER_SERVICE_REP_ASSIGNED_ACCOUNTS_FAILURE, payload: error });
        }
    };

    useEffect(() => {
        const fetchMonitoringLogsAbortController: AbortController | null = new AbortController();
        fetchMonitoringLogs(fetchMonitoringLogsAbortController);
        fetchAllAccountsViaStore();
        return () => {
            isMounted.current = false;
            if (fetchMonitoringLogsAbortController) fetchMonitoringLogsAbortController.abort();
        };
    }, []);

    useEffect(() => {
        const fetchAccountAssignmentsAbortController: AbortController | null = new AbortController();
        fetchAccountAssignments(fetchAccountAssignmentsAbortController);
        return () => {
            if (fetchAccountAssignmentsAbortController) fetchAccountAssignmentsAbortController.abort();
        };
    }, [user?.userName]);

    useEffect(() => {
        mapAccountsVisibleToUsers();
    }, [state.accounts, state.repAssignedAccounts]);
    useEffect(() => {
        mapContractsToAccounts();
    }, [state.contracts]);

    return (
        <CustomerServiceDashboardContext.Provider value={{ state, dispatch }}>
            {children}
        </CustomerServiceDashboardContext.Provider>
    );
};
